import Carousel from 'react-material-ui-carousel'
import { Typography, Stack, Pagination, useMediaQuery, useTheme } from '@mui/material'
import { generatePath, useNavigate } from 'react-router-dom'
import navigation from '../../config/navigation'
import { ProjectAcronymImages, useGetAcronymsImagesQuery } from '../../store/redux/reducers/projectsSlice'
import { useEffect, useState } from 'react'
import { RequestProps } from '../../store/types'
import AvatarComponent from '../../components/avatar/AvatarComponent'
import MasonryTile from '../../components/Masonry/MasonryTile'
import { useSelector } from 'react-redux'
import { getCurrentMode } from '../../store/redux/reducers/darkModeSlice'

type Slide = {
  id: string
  avatar: string | undefined
  extension: string | undefined
  title: string
}

const projectToSlide = (project: ProjectAcronymImages): Slide => {
  return {
    id: project.id,
    avatar: project.avatar,
    extension: project.extension,
    title: project.name,
  }
}

const projectPageSize = 5

const initialRequestProps: RequestProps = {
  page: 0,
  size: projectPageSize,
  favorite: true,
}

const ProjectsCarousel = () => {
  const [carouselIndex, setCarouselIndex] = useState(0)
  const [projectIndex, setProjectIndex] = useState(0)
  const [projects, setProjects] = useState<ProjectAcronymImages[]>([])
  const [projectsCount, setProjectsCount] = useState(0)
  const [requestProps, setRequestProps] = useState<RequestProps>(initialRequestProps)
  const theme = useTheme()

  const isUpSm = useMediaQuery(theme.breakpoints.up('sm'))

  const { data: projectsResponse, isSuccess } = useGetAcronymsImagesQuery(requestProps)
  useEffect(() => {
    if (isSuccess) {
      setProjects(projectsResponse.content)
      setProjectsCount(projectsResponse.totalElements)
    }
  }, [projectsResponse, isSuccess])

  useEffect(() => {
    const newPage = Math.floor(projectIndex / projectPageSize)
    if (newPage !== requestProps.page) {
      setRequestProps({ ...requestProps, page: newPage, size: projectPageSize })
    }

    setCarouselIndex(projectIndex % projectPageSize)
  }, [projectIndex, requestProps])

  return (
    <>
      {projects && projects.length > 0 && (
        <Stack
          width="100%"
          height="100%"
          direction="column"
          alignItems="center"
          justifyContent="center"
          sx={{ bgcolor: 'background.elevation1' }}
          gap={2}
          px="10%"
        >
          <Carousel
            navButtonsAlwaysInvisible={true}
            cycleNavigation={false}
            autoPlay={false}
            changeOnFirstRender={false}
            animation="fade"
            duration={100}
            indicators={false}
            swipe={false}
            strictIndexing={false}
            index={carouselIndex}
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {projects.map(project => (
              <CarouselItem slide={projectToSlide(project)} key={`${project.id}-carouselSlideKey`} />
            ))}
          </Carousel>
          <Stack width="100%" height="10%" alignItems="center" justifyContent="center">
            <Pagination
              count={projectsCount}
              size={'small'}
              showFirstButton={isUpSm}
              showLastButton={isUpSm}
              siblingCount={isUpSm ? 1 : 0}
              boundaryCount={0}
              onChange={(_, page) => {
                setProjectIndex(page - 1)
              }}
            />
          </Stack>
        </Stack>
      )}
    </>
  )
}

type CarouselItemProps = {
  slide: Slide
}

const CarouselItem = ({ slide }: CarouselItemProps) => {
  const navigate = useNavigate()
  const id = slide.id
  const mode = useSelector(getCurrentMode)
  return (
    <Stack width="100%" height="100%" direction="column" alignItems="center" justifyContent="center" py={1}>
      <MasonryTile
        sx={{
          boxShadow: mode === 'dark' ? '0px 1px 5px 0px rgba(0, 0, 0, 0.52)' : '',
        }}
        onClick={() => navigate(generatePath(navigation.projectDetails, { id }))}
      >
        <Stack width="200px" height="150px" direction="column" alignItems="center" justifyContent="center">
          <Stack width="100%" height="100%" justifyContent="center" alignItems="center">
            <AvatarComponent
              avatar={slide.avatar}
              extension={slide.extension}
              alt={slide.title + '-project-avatar'}
              size={72}
            />
          </Stack>
          <Stack direction="column" alignItems="center" justifyContent="center">
            <Typography
              variant="h4"
              component="h2"
              sx={{
                color: 'text.lighter',
                textAlign: 'center',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '180px',
              }}
            >
              {slide.title}
            </Typography>
          </Stack>
        </Stack>
      </MasonryTile>
    </Stack>
  )
}

export default ProjectsCarousel
